import type { AgentVersion, VersionChange } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Cpu, FileText, GitBranch, Hourglass, Minus, Plus, RefreshCw, Scale, ShieldHalf, Wrench } from "lucide-react";
import { type ReactNode, useState } from "react";
import { z } from "zod";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTooltip, SERIES } from "@/components/domain/charts";
import { Hash } from "@/components/domain/code";
import { TimeAgo } from "@/components/domain/misc";
import { Delta } from "@/components/domain/score";
import { QueryState } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { Tooltip as Tip } from "@/components/ui/tooltip";
import { Tag } from "@/features/agents/bits";
import { VersionCompare } from "@/features/agents/version-compare";
import { AXIS, CURSOR_LINE, GRID, niceDomain, stepTicks } from "@/features/agents/chart-kit";
import { q } from "@/lib/api";
import { fmtDateTime, fmtInt } from "@/lib/format";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  /** Version labels for Compare (A = baseline, B = candidate). */
  a: z.string().min(1).optional().catch(undefined),
  b: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/agents/$agentId/versions")({
  validateSearch: searchSchema,
  component: VersionsTab,
});

const CHANGE: Array<{ key: keyof AgentVersion["changes"]; label: string; icon: ReactNode }> = [
  { key: "prompt", label: "Prompt", icon: <FileText /> },
  { key: "model", label: "Model", icon: <Cpu /> },
  { key: "tools", label: "Tools", icon: <Wrench /> },
];

const CHANGE_ICON: Record<VersionChange["kind"], ReactNode> = {
  model: <Cpu />,
  prompt: <FileText />,
  tool_added: <Plus />,
  tool_removed: <Minus />,
  tool_changed: <RefreshCw />,
  authority_added: <Scale />,
  authority_removed: <Scale />,
};

const SHOWN = 6;

/** What the version changed, entry by entry; authority-widening entries are flagged. */
function Changelog({ entries, first }: { entries: VersionChange[]; first: boolean }) {
  const [all, setAll] = useState(false);
  if (!entries.length) return null;
  const shown = all ? entries : entries.slice(0, SHOWN);
  return (
    <div className="mt-2">
      <ul className="flex flex-col gap-1" aria-label={first ? "What this version started with" : "What changed"}>
        {shown.map((e, i) => (
          <li key={`${e.kind}-${i}`} className="flex items-start gap-2 text-xs">
            <span className={cn("mt-0.5 shrink-0 [&_svg]:size-3.5", e.kind === "tool_removed" || e.kind === "authority_removed" ? "text-fg-3" : "text-fg-2")} aria-hidden>
              {CHANGE_ICON[e.kind]}
            </span>
            <span className="min-w-0 text-fg-2">
              {e.kind.startsWith("authority") ? <span className="text-fg-3">{e.kind === "authority_added" ? "New rule: " : "Rule dropped: "}</span> : null}
              {e.text}
              {e.detail ? <span className="ml-1 font-mono text-[11px] text-fg-3">{e.detail}</span> : null}
            </span>
            {e.material ? (
              <Tip content="Widens what the agent can do: a new or redefined side-effecting tool, a new model, or a prompt that raises its authority.">
                <ShieldHalf className="mt-0.5 size-3.5 shrink-0 cursor-default text-warning-text" aria-label="Widens authority" />
              </Tip>
            ) : null}
          </li>
        ))}
      </ul>
      {entries.length > SHOWN ? (
        <button type="button" onClick={() => setAll((a) => !a)} className="mt-1 cursor-pointer text-xs text-accent hover:underline">
          {all ? "Show less" : `Show all ${entries.length} changes`}
        </button>
      ) : null}
    </div>
  );
}

function VersionsTab() {
  const { agentId } = Route.useParams();
  const versions = useQuery(q.agentVersions(agentId));
  return (
    <QueryState
      query={versions}
      loading={
        <div className="flex flex-col gap-4">
          <Skeleton className="h-52" />
          <Skeleton className="h-96" />
        </div>
      }
    >
      {(v) =>
        v.length ? (
          <VersionsBody versions={v} />
        ) : (
          <Card>
            <EmptyState icon={<GitBranch />} title="No versions observed yet" description="Versions appear when a new agent version hash shows up in production traces." />
          </Card>
        )
      }
    </QueryState>
  );
}

function VersionsBody({ versions }: { versions: AgentVersion[] }) {
  const { agentId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // Newest first for the timeline; chronological for the chart.
  const newestFirst = [...versions].sort((a, b) => Date.parse(b.deployedAt) - Date.parse(a.deployedAt));
  const material = versions.filter((v) => v.materialChange).length;
  const known = (l?: string) => (l && versions.some((v) => v.label === l) ? l : undefined);
  // Default comparison: the current version (B) against the one before it (A).
  const b = known(search.b) ?? newestFirst[0]!.label;
  const a = known(search.a) ?? newestFirst.find((v) => v.label !== b)?.label ?? b;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Score by version"
          description="ARS measured while each version was live. Material changes trigger re-rating; their score is withheld until the soak completes."
          actions={
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-3">
              <span className="inline-block size-2.5 rounded-full border-2" style={{ borderColor: SERIES[0] }} aria-hidden />
              {material} material {material === 1 ? "change" : "changes"}
            </span>
          }
        />
        <CardBody className="pb-3">
          <VersionScoreChart versions={[...newestFirst].reverse()} />
        </CardBody>
      </Card>

      {versions.length > 1 ? (
        <VersionCompare agentId={agentId} versions={versions} a={a} b={b} onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true, resetScroll: false })} />
      ) : null}

      <Card>
        <CardHeader title="Changelog" description={`${versions.length} versions · model, prompt, tool and authority-rule changes per version, detected from traces`} />
        <ol className="flex flex-col">
          {newestFirst.map((v, i) => (
            <VersionItem key={v.id} version={v} prev={newestFirst[i + 1]} current={i === 0} last={i === newestFirst.length - 1} />
          ))}
        </ol>
      </Card>
    </div>
  );
}

function VersionScoreChart({ versions }: { versions: AgentVersion[] }) {
  const rows = versions.map((v) => ({ label: v.label, ars: v.ars, material: v.materialChange }));
  const scored = rows.flatMap((r) => (r.ars === null ? [] : [r.ars]));
  if (scored.length < 2) return <p className="py-10 text-center text-xs text-fg-3">Not enough scored versions to chart yet.</p>;
  const domain = niceDomain(Math.min(...scored) - 10, Math.max(...scored) + 10, 25);
  const range = domain[1] - domain[0];
  const ticks = stepTicks(domain, [25, 50, 100].find((s) => range / s <= 5) ?? 100);
  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid {...GRID} />
        <XAxis dataKey="label" {...AXIS} tick={{ ...AXIS.tick, fontFamily: "var(--font-mono)" }} interval={0} />
        <YAxis {...AXIS} width={40} domain={domain} ticks={ticks} interval={0} />
        <Tooltip cursor={CURSOR_LINE} content={<ChartTooltip formatValue={(v) => (Number.isFinite(v) ? v : "Rating…")} />} />
        <Line
          type="linear"
          dataKey="ars"
          name="ARS"
          stroke={SERIES[0]}
          strokeWidth={2}
          connectNulls={false}
          isAnimationActive={false}
          dot={(p: { cx?: number; cy?: number; index?: number; payload?: (typeof rows)[number] }) => {
            if (p.cx === undefined || p.cy === undefined || !p.payload || p.payload.ars === null) return <g key={p.index} />;
            // Material-change versions get a hollow ring so they read without relying on color.
            return p.payload.material ? (
              <circle key={p.index} cx={p.cx} cy={p.cy} r={5} fill="var(--surface)" stroke={SERIES[0]} strokeWidth={2} />
            ) : (
              <circle key={p.index} cx={p.cx} cy={p.cy} r={3.5} fill={SERIES[0]} stroke="var(--surface)" strokeWidth={1.5} />
            );
          }}
          activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--surface)" }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function VersionItem({ version: v, prev, current, last }: { version: AgentVersion; prev?: AgentVersion; current: boolean; last: boolean }) {
  const changed = CHANGE.filter((c) => v.changes[c.key]);
  const modelChanged = prev && prev.model !== v.model;
  const delta = v.ars !== null && prev?.ars != null ? v.ars - prev.ars : undefined;
  return (
    <li className="relative grid grid-cols-[28px_minmax(0,1fr)_auto] gap-x-3 px-4 py-3.5 [&:not(:last-child)]:border-b">
      {/* Rail */}
      <div className="relative flex justify-center" aria-hidden>
        {!last ? <span className="absolute bottom-[-19px] top-4 w-px bg-border-strong" /> : null}
        <span
          className={cn(
            "relative mt-1 flex size-3 items-center justify-center rounded-full border-2",
            v.materialChange ? "border-warning bg-surface" : current ? "border-accent bg-accent" : "border-border-strong bg-surface-3",
          )}
        />
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[13px] font-semibold text-fg">{v.label}</span>
          {current ? <Badge tone="accent">Current</Badge> : null}
          {v.materialChange ? (
            <Tip content="Material change: the agent is re-rated. Its score is re-established over a soak window before coverage terms change.">
              <Badge tone="warning" className="cursor-default">
                <AlertTriangle />
                Material change
              </Badge>
            </Tip>
          ) : null}
          <Hash value={v.hash} n={15} />
        </div>
        <p className="mt-1 text-[13px] text-fg">{v.summary}</p>
        <Changelog entries={v.changelog ?? []} first={!prev} />
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-fg-3">
          <span className="inline-flex items-center gap-1.5">
            Deployed <span className="text-fg-2 tabular">{fmtDateTime(v.deployedAt)}</span> · <TimeAgo iso={v.deployedAt} className="text-fg-3" />
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Cpu className="size-3.5" aria-hidden />
            {modelChanged ? (
              <span className="font-mono text-[11.5px]">
                <span className="text-fg-3 line-through">{prev.model}</span> <span className="text-fg-3">→</span> <span className="text-fg">{v.model}</span>
              </span>
            ) : (
              <span className="font-mono text-[11.5px] text-fg-2">{v.model}</span>
            )}
          </span>
          <span className="inline-flex flex-wrap items-center gap-1" aria-label="Changed components">
            {changed.length ? (
              changed.map((c) => <Tag key={c.key}>{c.icon}{c.label}</Tag>)
            ) : (
              <span className="text-fg-3">Config only</span>
            )}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 text-right">
        {v.ars === null ? (
          <Tip content="Re-rating in progress: score withheld until the post-change soak completes.">
            <span className="inline-flex cursor-default items-center gap-1 text-[13px] text-fg-2">
              <Hourglass className="size-3.5 text-fg-3" aria-hidden />
              Rating…
            </span>
          </Tip>
        ) : (
          <span className="inline-flex items-baseline gap-2">
            {delta !== undefined ? <Delta value={delta} /> : null}
            <span className="text-base font-semibold tabular text-fg">{v.ars}</span>
            <span className="text-[11px] text-fg-3">ARS</span>
          </span>
        )}
        <span className="text-xs tabular text-fg-3">
          <span className="text-fg-2">{fmtInt(v.sessions)}</span> sessions
        </span>
      </div>
    </li>
  );
}
